import {
  Button, Flex, Text
} from '@huspy/briks-web';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import BankIcon from '@components/v2/BankIcon';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { BANK_APPLICATION_STATUS, BANK_SUBMISSION_CHANNEL_TYPE } from '@modules/opportunities/entities/bankApplication/const';
import { CaseCreationSubmitApplicationsRoute } from '@modules/opportunities/presentation/routes';
import useGetEmailPreferences from '@modules/clients/entities/client/query/useGetEmailPreferences';
import { Opportunity } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { BankSubmissionStatusBadge } from '@modules/opportunities/presentation/pages/CaseCreation/components/BankSubmissionStatusBadge';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import useCredits from '@modules/core/hooks/useCredits';
import { USER_EVENTS } from '@shared/analytics/events';
import { DirectToBankSubmissionSlido } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/DirectToBankSubmissionSlido';
import {
  BANK_SUBMISSION_TYPE,
  BankSubmissionType,
  EMAIL_CHANNEL_PROVIDER,
  EmailChannelProviderType,
// eslint-disable-next-line max-len
} from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/DirectToBankSubmissionSlido/const';
import { BrokerageSubmissionSlido } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/SubmitApplications/components/BrokerageSubmissionSlido';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { getDirectBankActionNameBySubmissionsNumber, getHuspyActionNameBySubmissionsNumber } from './utils';
import { VaultReviewSlido } from './VaultReviewSlido';
import { useVaultReview } from './VaultReviewSlido/useVaultReview';

const baseTranslation = 'opportunity.caseSubmission.submitApplications.bankAndHuspySubmission.banksCard';

const getEmailChannelProviderDefaultValue = (
  emailProvider: EmailChannelProviderType
) => {
  if (!emailProvider || emailProvider === EMAIL_CHANNEL_PROVIDER.THIRD_PARTY) {
    return EMAIL_CHANNEL_PROVIDER.THIRD_PARTY;
  }

  return EMAIL_CHANNEL_PROVIDER.SELF;
};

export const BankCard = ({
  bankApplication,
  opportunity,
}: {
  bankApplication: BankApplication;
  opportunity: Opportunity;
}) => {
  const { bank_details, bank_submission_channel_type } = bankApplication;
  const { t } = useTranslation();
  const [opened, { close, open }] = useDisclosure(false);
  const [actionType, setActionType] = useState<null | BankSubmissionType>(null);
  const { bank_application_id } = CaseCreationSubmitApplicationsRoute.useSearch();
  const { data: getEmailAuthInfo, isLoading } = useGetEmailPreferences(
    opportunity.referrer_external_id
  );
  const {
    isOpened: isVaultReviewOpened,
    handleVaultReview,
    close: closeVaultReviewSlido,
    isSubmitVaultLoading,
    vaultError,
  } = useVaultReview(
    opportunity.opportunity_external_id,
    opportunity.client_external_id!
  );
  const { callCreditsAction } = useCredits();
  const reset = () => {
    close();
    setActionType(null);
  };
  const { isFeatureActive } = useFeatureFlags();

  const isSubmitted = bankApplication.status !== BANK_APPLICATION_STATUS.draft;

  useEffect(() => {
    if (bank_application_id === bankApplication.bank_application_external_id) {
      open();
      setActionType(BANK_SUBMISSION_TYPE.DIRECT_TO_BANK);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDirectToBankClick = async () => {
    setActionType(BANK_SUBMISSION_TYPE.DIRECT_TO_BANK);
    const shouldOpenVaultReview = await handleVaultReview();
    if (shouldOpenVaultReview) {
      return;
    }
    const numberOfDirectBankSubmissions = opportunity.bank_applications.reduce((acc, value) => {
      if (value.bank_submission_channel_type === BANK_SUBMISSION_CHANNEL_TYPE.directBank) {
        return acc + 1;
      }

      return acc;
    }, 0);

    const actionName = getDirectBankActionNameBySubmissionsNumber(numberOfDirectBankSubmissions);

    callCreditsAction(actionName, () => {
      open();
    }, (error: Error) => {
      trackAmplitudeEvent(USER_EVENTS.CASE.CASE_SUBMISSION_FAILED, {
        useContext: true,
        error_message: error.message,
        blocked: 1,
        case_id: opportunity.opportunity_external_id,
        destination_type: 'Bank',
        destinations: [bankApplication.bank_application_external_id],
        application_rank: numberOfDirectBankSubmissions + 1,
      });
    });
  };

  const onSubmitThroughHuspyClick = async () => {
    setActionType(BANK_SUBMISSION_TYPE.SUBMIT_THROUGH_HUSPY);
    const shouldOpenVaultReview = await handleVaultReview();
    if (shouldOpenVaultReview) {
      return;
    }
    const numberOfSubmissionsToHuspy = opportunity.bank_applications.reduce((acc, value) => {
      if (value.bank_submission_channel_type === BANK_SUBMISSION_CHANNEL_TYPE.huspy) {
        return acc + 1;
      }

      return acc;
    }, 0);

    const actionName = getHuspyActionNameBySubmissionsNumber(numberOfSubmissionsToHuspy);

    callCreditsAction(actionName, () => {
      open();
    }, (error: Error) => {
      trackAmplitudeEvent(
        USER_EVENTS.CASE.CASE_SUBMISSION_FAILED,
        {
          useContext: true,
          blocked: 1,
          error_message: error.message,
          application_rank: numberOfSubmissionsToHuspy + 1,
          destination_type: 'Huspy',
          destinations: ['Huspy'],
          opportunity_id: opportunity.opportunity_external_id,
        }
      );
    });
  };

  if (isLoading) {
    return <></>;
  }

  return (
    <Flex direction='column' gap={ 2 }>
      <Flex
        backgroundColor='white'
        borderRadius='4'
        padding='6'
        align='center'
        justify='space-between'
        borderWidth='1px'
      >
        <Flex align='center' gap='3' w='30%'>
          <BankIcon
            icon={ bank_details.icon }
            height='48px'
            width='48px'
            pos='relative'
          />
          <Text>{bank_details.name}</Text>
        </Flex>
        <Flex gap='2' w='70%' justify='end'>
          {isSubmitted ? (
            <BankSubmissionStatusBadge
              bankSubmissionChannelType={ bank_submission_channel_type }
            />
          ) : (
            <>
              <Button
                type='button'
                variant='primary'
                w='fit-content'
                data-test='send-through-huspy-btn'
                data-testid='send-through-huspy-btn'
                onClick={ onSubmitThroughHuspyClick }
                loading={ isSubmitVaultLoading && actionType === BANK_SUBMISSION_TYPE.SUBMIT_THROUGH_HUSPY }
              >
                {t(`${baseTranslation}.sendThroughHuspy`)}
              </Button>
              {isFeatureActive('opportunities.directToBankSubmission')
                && (
                  <Button
                    type='button'
                    variant='outline'
                    w='fit-content'
                    data-test='send-directly-bank-btn'
                    data-testid='send-directly-bank-btn'
                    onClick={ onDirectToBankClick }
                    loading={ isSubmitVaultLoading && actionType === BANK_SUBMISSION_TYPE.DIRECT_TO_BANK }
                  >
                    {t(`${baseTranslation}.sendDirectlyBank`)}
                  </Button>
                )}
            </>
          )}
        </Flex>
      </Flex>
      {
        opened
        && actionType === BANK_SUBMISSION_TYPE.DIRECT_TO_BANK
        && (
          <DirectToBankSubmissionSlido
            opportunity={ opportunity }
            opened={ opened && actionType === BANK_SUBMISSION_TYPE.DIRECT_TO_BANK }
            close={ reset }
            bankApplication={ bankApplication }
            emailChannelProviderDefaultValue={ getEmailChannelProviderDefaultValue(
              getEmailAuthInfo?.emailProvider!
            ) }
            isThirdPartyProvider={ getEmailAuthInfo?.isVerified! }
          />
        )
      }
      {
        opened
        && actionType === BANK_SUBMISSION_TYPE.SUBMIT_THROUGH_HUSPY
        && (
          <BrokerageSubmissionSlido
            opened={ opened && actionType === BANK_SUBMISSION_TYPE.SUBMIT_THROUGH_HUSPY }
            close={ reset }
            bankApplications={ [bankApplication] }
            opportunityId={ opportunity.opportunity_external_id }
            clientExternalId={ opportunity.client_external_id! }
            oppportunity={ opportunity }
            isSubmitAll={ false }
          />
        )
      }
      {
        isVaultReviewOpened
        && (
          <VaultReviewSlido
            opened={ isVaultReviewOpened }
            close={ closeVaultReviewSlido }
            opportunityId={ opportunity.opportunity_external_id }
            vaultError={ vaultError }
          />
        )
      }

    </Flex>
  );
};
